'use client';

import { FormEvent, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { ApiError, resetPassword } from '@torlin/api-client';
import { localePath, loginCopy, type Locale } from '@/i18n/public';

const apiUrl = process.env.NEXT_PUBLIC_API_URL ?? '';

const requirementCopy = {
  cs: {
    length: 'Alespoň 10 znaků',
    lowercase: 'Alespoň jedno malé písmeno',
    uppercase: 'Alespoň jedno velké písmeno',
    number: 'Alespoň jedno číslo',
    symbol: 'Alespoň jeden symbol',
    match: 'Hesla se musí shodovat',
  },
  en: {
    length: 'At least 10 characters',
    lowercase: 'At least one lowercase letter',
    uppercase: 'At least one uppercase letter',
    number: 'At least one number',
    symbol: 'At least one symbol',
    match: 'Passwords must match',
  },
  de: {
    length: 'Mindestens 10 Zeichen',
    lowercase: 'Mindestens ein Kleinbuchstabe',
    uppercase: 'Mindestens ein Großbuchstabe',
    number: 'Mindestens eine Zahl',
    symbol: 'Mindestens ein Symbol',
    match: 'Die Passwörter müssen übereinstimmen',
  },
} as const;

export function PasswordResetForm({ locale, token, email }: { locale: Locale; token: string; email: string }) {
  const router = useRouter();
  const copy = loginCopy[locale];
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const requirements = [
    { valid: password.length >= 10, label: requirementCopy[locale].length },
    { valid: /[a-z]/.test(password), label: requirementCopy[locale].lowercase },
    { valid: /[A-Z]/.test(password), label: requirementCopy[locale].uppercase },
    { valid: /\d/.test(password), label: requirementCopy[locale].number },
    { valid: /[^\p{L}\p{N}\s]/u.test(password), label: requirementCopy[locale].symbol },
    { valid: password.length > 0 && password === confirmation, label: requirementCopy[locale].match },
  ];
  const formValid = requirements.every(({ valid }) => valid);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!token.trim() || !email.trim()) {
      setError(copy.resetInvalid);
      return;
    }
    if (password !== confirmation) {
      setError(copy.confirmPassword);
      return;
    }
    setLoading(true);
    try {
      await resetPassword(apiUrl, token, email, password, confirmation, locale);
      setSuccess(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? Object.values(caught.errors).flat()[0] ?? caught.message : copy.resetInvalid);
    } finally {
      setLoading(false);
    }
  }

  if (success) return <Stack spacing={3}><Alert severity="success">{copy.resetSuccess}</Alert><Button variant="contained" onClick={() => router.replace(localePath(locale, '/login'))}>{copy.backToLogin}</Button></Stack>;

  return <Box component="form" onSubmit={submit}><Stack spacing={3}>
    <Box><Typography variant="h4" component="h1" gutterBottom>{copy.resetTitle}</Typography><Typography color="text.secondary">{copy.resetIntro}</Typography></Box>
    {error && <Alert severity="error">{error}</Alert>}
    <TextField autoFocus fullWidth type="password" label={copy.newPassword} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" helperText={copy.passwordRequirements} required />
    {requirements.some(({ valid }) => !valid) && <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
      {requirements.filter(({ valid }) => !valid).map(({ label }) => <Typography component="li" key={label} variant="body2" color="error">{label}</Typography>)}
    </Stack>}
    <TextField fullWidth type="password" label={copy.confirmPassword} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" required />
    <Button type="submit" size="large" variant="contained" disabled={loading || !formValid}>{loading ? <CircularProgress size={24} color="inherit" /> : copy.resetAction}</Button>
    <Button type="button" color="inherit" onClick={() => router.replace(localePath(locale, '/login'))}>{copy.backToLogin}</Button>
  </Stack></Box>;
}
